import type { TwnBadgeProps } from '@/components/atoms/prime/badge/twn-badge/TwnBadge.types'

/**
 * Lifecycle of a location's EXO certification — a temporary placeholder until the Commissioner
 * for Revenue grants the real number. Mirrors `Domain\Fiscal\Malta\Enums\ExoStatus`.
 *
 * The labels live here rather than coming off the wire because the configuration *trace* endpoint
 * returns raw stored values with no `options` array — unlike the configuration *index*, which
 * serves enum labels via `ConfigurationKeyDefinition::enum()`. Same arrangement as
 * FISCAL_MODE_LABELS.
 */
const EXO_STATUSES = {
  PLACEHOLDER: 'placeholder',
  GRANTED: 'granted'
} as const

type ExoStatus = (typeof EXO_STATUSES)[keyof typeof EXO_STATUSES]

const EXO_STATUS_LABELS: Record<ExoStatus, string> = {
  [EXO_STATUSES.PLACEHOLDER]: 'Pending (awaiting EXO number)',
  [EXO_STATUSES.GRANTED]: 'Granted'
}

const EXO_STATUS_SEVERITIES: Record<ExoStatus, TwnBadgeProps['severity']> = {
  [EXO_STATUSES.PLACEHOLDER]: 'warn',
  [EXO_STATUSES.GRANTED]: 'success'
}

/** The configuration key the indicator traces. */
const EXO_STATUS_KEY = 'fiscal.mt.exo.status'

/** Anything stored that isn't a known member — a value written before an enum change, say. */
const isExoStatus = (value: unknown): value is ExoStatus =>
  value === EXO_STATUSES.PLACEHOLDER || value === EXO_STATUSES.GRANTED

export { EXO_STATUSES, EXO_STATUS_LABELS, EXO_STATUS_SEVERITIES, EXO_STATUS_KEY, isExoStatus }
export type { ExoStatus }
